import { useEffect, MutableRefObject } from 'react';

import isIntersectionObserverSupported from './intersectionObserverSupport';

type Callback = () => void;
type Entries = IntersectionObserverEntry[];

const listenerCallbacks: WeakMap<Element, Callback> = new WeakMap();
let observer: IntersectionObserver;

function handleIntersections(entries: Entries) {
  entries.forEach((entry) => {
    if (listenerCallbacks.has(entry.target)) {
      const cb = listenerCallbacks.get(entry.target);

      if (entry.isIntersecting || entry.intersectionRatio > 0) {
        observer.unobserve(entry.target);
        listenerCallbacks.delete(entry.target);
        cb && cb();
      }
    }
  });
}

function getIntersectionObserver() {
  if (observer == null && typeof IntersectionObserver === 'function') {
    observer = new IntersectionObserver(handleIntersections, {
      rootMargin: '100px',
      threshold: 0.15,
    });
  }
  return observer;
}

export default (
  element: MutableRefObject<HTMLElement | null>,
  callback: () => void,
): void => {
  useEffect(() => {
    const target = element.current;
    const hasIntersectionObserverSupport = isIntersectionObserverSupported();

    if (target == null) {
      return;
    }

    if (!hasIntersectionObserverSupport) {
      callback && callback();
    }

    const observer = getIntersectionObserver();
    listenerCallbacks.set(target, callback);
    observer?.observe(target);

    return () => {
      listenerCallbacks.delete(target);
      observer.unobserve(target);
    };
  }, [callback, element]);

  return;
};
